/** @format */
import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Card from '../../components/ui/card';
import MRRIcon from '../../components/ui/icons/MRRIcon';
import { FiTrendingUp, FiTrendingDown } from 'react-icons/fi';

// Constants for MRR thresholds
const MRR_THRESHOLDS = {
	HIGH_AMOUNT: 50000,
};

const MRRTrendCard = ({ 
	currentMRR = 0, 
	trendPercentage = 0, 
	mrrTrendData = [], 
	isLoading = false 
}) => {
	// Determine trend direction and styling
	const isPositiveTrend = trendPercentage >= 0;
	const TrendIcon = isPositiveTrend ? FiTrendingUp : FiTrendingDown;

	// Format currency display
	const formatCurrency = (amount) => {
		return new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: 'USD',
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(amount);
	};

	// Process MRR trend data for chart display - aggregate by week for better readability
	const chartData = (() => {
		if (!mrrTrendData || mrrTrendData.length === 0) return [];
		
		// Validate data structure - filter out invalid entries
		const validData = mrrTrendData.filter(item => 
			item && 
			item.date instanceof Date && 
			!isNaN(item.date.getTime()) && 
			typeof item.mrr === 'number' && 
			!isNaN(item.mrr)
		);

		if (validData.length === 0) {
			console.warn('MRRTrendCard: No valid data available for chart rendering');
			return [];
		}
		
		const weeklyData = [];
		const weeksToShow = Math.ceil(validData.length / 7); // Show all weeks for 6-month period (~26 weeks)
		
		for (let weekIndex = 0; weekIndex < weeksToShow; weekIndex++) {
			const startDayIndex = weekIndex * 7;
			const endDayIndex = Math.min(startDayIndex + 7, validData.length);
			
			if (startDayIndex >= validData.length) break;
			
			// Use direct array access to avoid creating intermediate arrays
			const lastDayOfWeek = validData[endDayIndex - 1];
			if (!lastDayOfWeek) continue;
			
			const weekDate = lastDayOfWeek.date;
			
			weeklyData.push({
				name: weekDate ? weekDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : `Week ${weekIndex + 1}`,
				mrr: lastDayOfWeek.mrr || 0,
				date: weekDate
			});
		}
		
		return weeklyData;
	})();

	// Custom tooltip for the chart
	const CustomTooltip = ({ active, payload, label }) => {
		if (active && payload && payload.length) {
			return (
				<div className="bg-white border border-gray-200 rounded-lg p-3 shadow-lg">
					<p className="text-sm font-montserrat-medium text-gray-900">{label}</p>
					<p className="text-sm font-montserrat-regular text-gray-600">
						MRR: <span className="font-montserrat-medium text-primary">{formatCurrency(payload[0].value)}</span>
					</p>
				</div>
			);
		}
		return null;
	};

	if (isLoading) {
		return (
			<Card className="overflow-hidden animate-pulse">
				<div className="p-4 sm:p-6">
					<div className="flex justify-between items-start mb-4">
						<div>
							<div className="h-4 bg-gray-200 rounded w-40 mb-2"></div>
							<div className="h-8 bg-gray-200 rounded w-24"></div>
						</div>
						<div className="rounded-full p-3 sm:p-4 bg-gray-200">
							<div className="h-6 w-6 sm:h-7 sm:w-7 bg-gray-300 rounded"></div>
						</div>
					</div>
					<div className="flex items-center mb-4">
						<div className="h-4 bg-gray-200 rounded w-20 mr-2"></div>
						<div className="h-4 bg-gray-200 rounded w-24"></div>
					</div>
					<div className="h-20 bg-gray-200 rounded"></div>
				</div>
			</Card>
		);
	}

	return (
		<Card className="overflow-hidden bg-white border border-gray-100">
			<div className="p-4 sm:p-6">
				{/* Header Section */}
				<div className="flex justify-between items-start mb-4">
					<div>
						<h3 className="text-sm font-montserrat-medium text-gray-700 uppercase tracking-wide">
							Monthly Recurring Revenue
						</h3>
						<p className="text-2xl sm:text-3xl font-glacial text-gray-900 mt-2">
							{formatCurrency(currentMRR)}
						</p>
					</div>
					<div className="rounded-full p-3 sm:p-4 bg-gray-100">
						<MRRIcon
							className="h-6 w-6 sm:h-7 sm:w-7 text-clientBlack"
							trend={trendPercentage > 0 ? "positive" : trendPercentage < 0 ? "negative" : "neutral"}
							amount={currentMRR}
							showPinkAccent={currentMRR > MRR_THRESHOLDS.HIGH_AMOUNT}
						/>
					</div>
				</div>

				{/* Refined Trend Indicator */}
				<div className="flex items-center space-x-3 px-4 py-3 bg-gray-50 rounded-lg border border-gray-200 mb-4">
					<TrendIcon className={`h-4 w-4 ${
						isPositiveTrend ? 'text-green-600' : 'text-red-600'
					}`} />
					<span className="text-sm font-montserrat-regular text-gray-700">
						<span className={`font-montserrat-medium ${
							isPositiveTrend ? 'text-green-700' : 'text-red-700'
						}`}>
							{Math.abs(trendPercentage).toFixed(1)}%
						</span>
						{' '}vs 6 months ago
					</span>
				</div>

				{/* MRR Trend Chart */}
				<div className="h-40 sm:h-48 bg-white border border-gray-200 rounded-lg p-2 sm:p-4">
					<div className="text-xs font-montserrat-medium text-gray-600 mb-2 sm:mb-4">
						6-Month MRR Trend
					</div>
					{chartData.length > 0 ? (
						<ResponsiveContainer width="100%" height="100%">
							<LineChart data={chartData} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
								<CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
								<XAxis
									dataKey="name"
									tick={{ fontSize: 8, fill: '#6b7280' }}
									tickLine={{ stroke: '#d1d5db' }}
									axisLine={{ stroke: '#d1d5db' }}
									interval="preserveStartEnd"
									angle={-45}
									textAnchor="end"
									height={40}
								/>
								<YAxis
									tick={{ fontSize: 8, fill: '#6b7280' }}
									tickLine={{ stroke: '#d1d5db' }}
									axisLine={{ stroke: '#d1d5db' }}
									tickFormatter={(value) => `$${(value/1000).toFixed(0)}k`}
									width={35}
								/>
								<Tooltip content={<CustomTooltip />} />
								<Line
									type="monotone"
									dataKey="mrr"
									stroke="#CC1DB9"
									strokeWidth={2}
									dot={{ fill: '#CC1DB9', strokeWidth: 1, r: 2 }}
									activeDot={{ r: 4, stroke: '#CC1DB9', strokeWidth: 2 }}
								/>
							</LineChart>
						</ResponsiveContainer>
					) : (
						<div className="flex items-center justify-center h-full">
							<div className="text-center">
								<div className="text-sm font-montserrat-regular text-gray-500">
									No trend data available
								</div>
								<div className="text-xs font-montserrat-regular text-gray-400 mt-1">
									Chart will populate with subscription activity
								</div>
							</div>
						</div>
					)}
				</div>

				{/* Footer Info */}
				<div className="mt-4 pt-4 border-t border-gray-100">
					<div className="flex justify-between items-center text-xs">
						<span className="font-montserrat-regular text-gray-500">
							Last updated: {new Date().toLocaleDateString()}
						</span>
						<span className="font-montserrat-medium text-gray-600">
							Based on active subscriptions
						</span>
					</div>
				</div>
			</div>
		</Card>
	);
};

export default MRRTrendCard;